import { OfficeBackButton } from '../shared/OfficeBackButton';
import { useBaseStandardUi } from '../../lib/hooks/useBaseStandardUi';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Users, Info, DollarSign, Ban as Bandage, UserX, Plus } from 'lucide-react';
import { useLeagueStore } from '../../lib/leagueStore';
import { useLanguageStore } from '../../lib/languageStore';
import { useLeagueMode } from '../../lib/hooks/useLeagueMode';
import { OtherTeamsTable } from './OtherTeamsTable';
import { OtherTeamsGoalieTable } from './OtherTeamsGoalieTable';
import { PositionFilterMyTeams } from '../MyTeams/PositionFilterMyTeams';
import { Position } from '../MyTeams/typesMyTeams';
import { getUserByLeaguSaufUserConnecter, getMyteamPlayer, getMyTeamGoalies, searchLeaguePlayers, addToWatchlist, removeFromWatchlist, getWatchlistPlayerIds, getUserDraftPicks } from '../../lib/supabase';
import { useUserStore } from '../../lib/userStore';
import { getLeagueSettings } from '../../lib/api/leagueSettings';
import { supabase } from '../../lib/supabase';
import { TeamLogo } from '../TeamLogo';
import toast from 'react-hot-toast';
import { usePageTracking } from '../../lib/hooks/usePageTracking';
import { useEmbedStore } from '../../lib/embedStore';
import { EmbedBackButton } from '../shared/EmbedBackButton';
import { PoolerFollowButton } from '../PoolerFollows/PoolerFollowButton';
import './OtherTeamsEmbed.css';

type TeamSection = 'MAIN' | 'RESERVES' | 'INJURED' | 'SCRATCH';
type ActiveTab = 'players' | 'goalies' | 'overview';

interface Team {
  users_user_id: number;
  username: string;
  nhl_team_abbrev?: string | null;
  initial_build_complete?: boolean | null;
}

interface LeagueSettings {
  salary_cap: boolean;
  max_salary_cap: number;
  salary_cap_reserve: boolean;
  max_salary_cap_reserve: number;
  max_injury: number;
}

const ARSENAL_FONTS_ID = 'arsenal-sports-fonts';
const ARSENAL_FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Anton&family=Open+Sans:wght@400;600;700&display=swap';

function useArsenalFonts(enabled: boolean) {
  React.useEffect(() => {
    if (!enabled) return;
    if (document.getElementById(ARSENAL_FONTS_ID)) return;
    const link = document.createElement('link');
    link.id = ARSENAL_FONTS_ID;
    link.rel = 'stylesheet';
    link.href = ARSENAL_FONTS_HREF;
    document.head.appendChild(link);
  }, [enabled]);
}

export function OtherTeams() {
  // Tracker cette page
  usePageTracking('otherteam');

  const isEmbed = useEmbedStore((state) => state.isEmbed);
  useArsenalFonts(isEmbed);

  const navigate = useNavigate();
  const { userId } = useParams<{ userId?: string }>();
  const selectedLeague = useLeagueStore((state) => state.selectedLeague);
  const hideDemotionCard = isEmbed && selectedLeague?.id === 138;
  const currentUserId = useUserStore((state) => state.user_id);
  const { language } = useLanguageStore();
  const { isStandardFlexible, isLoading: modeLoading } = useLeagueMode();
  const isBaseStandardUi = useBaseStandardUi(isStandardFlexible);
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedPosition, setSelectedPosition] = useState<Position>('ALL');
  // Default to overview view on load (and keep overview only)
  const [activeTab] = useState<ActiveTab>('overview');
  const [activeSection, setActiveSection] = useState<TeamSection>('MAIN');
  const [selectedTeam, setSelectedTeam] = useState<string>('');
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);
  const visibleTeams = React.useMemo(
    () => isStandardFlexible
      ? teams.filter((team) => team.initial_build_complete === true)
      : teams,
    [isStandardFlexible, teams]
  );
  const [playerSearchTerm, setPlayerSearchTerm] = useState('');
  const [playerSearchResults, setPlayerSearchResults] = useState<Array<{playerid:number; fullname:string; user_id:number; isGoalie:boolean}>>([]);
  // Team search (standard_flexible only)
  const [teamSearchTerm, setTeamSearchTerm] = useState('');
  const [teamSearchResults, setTeamSearchResults] = useState<Team[]>([]);
  const [teamSearchLoading, setTeamSearchLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [highlightPlayerId, setHighlightPlayerId] = useState<number | null>(null);
  const [players, setPlayers] = useState<any[]>([]);
  const [goalies, setGoalies] = useState<any[]>([]);
  const [injuredPlayers, setInjuredPlayers] = useState<any[]>([]);
  const [injuredGoalies, setInjuredGoalies] = useState<any[]>([]);
  const [scratchPlayers, setScratchPlayers] = useState<any[]>([]);
  const [scratchGoalies, setScratchGoalies] = useState<any[]>([]);
  // My team (left column on desktop)
  const [myPlayers, setMyPlayers] = useState<any[]>([]);
  const [myGoalies, setMyGoalies] = useState<any[]>([]);
  const [myInjuredPlayers, setMyInjuredPlayers] = useState<any[]>([]);
  const [myInjuredGoalies, setMyInjuredGoalies] = useState<any[]>([]);
  const [myScratchPlayers, setMyScratchPlayers] = useState<any[]>([]);
  const [myScratchGoalies, setMyScratchGoalies] = useState<any[]>([]);
  const [injuryDescriptions, setInjuryDescriptions] = useState<Array<{playerid: number, description: string}>>([]);
  const [draftPicks, setDraftPicks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [myLoading, setMyLoading] = useState(true);
  
  // Watchlist states
  const [watchlistPlayerIds, setWatchlistPlayerIds] = useState<Set<number>>(new Set());
  const [watchlistActionInProgress, setWatchlistActionInProgress] = useState(false);
  const [leagueSettings, setLeagueSettings] = useState<LeagueSettings | null>(null);
  const [currentSalaryCap, setCurrentSalaryCap] = useState<number | null>(null);
  const [currentReserveSalaryCap, setCurrentReserveSalaryCap] = useState<number | null>(null);
  // Compteurs de signatures et rétrogradations
  const [signingUsed, setSigningUsed] = useState<number>(0);
  const [demotionUsed, setDemotionUsed] = useState<number>(0);
  const [maxSigning, setMaxSigning] = useState<number>(15);
  const [maxDemotion, setMaxDemotion] = useState<number>(10);
  const [showSalaryInfo, setShowSalaryInfo] = useState(false);
  const [showPlayerMovements, setShowPlayerMovements] = useState(false);
  const sortConfig = { field: 'points', direction: 'desc' as const };
  const goalieSortConfig = { field: 'wins', direction: 'desc' as const };

  const handleViewPlayerDetails = (player: { playerid: number }) => {
    navigate(`/players/${player.playerid}`);
  };

  // Watchlist handlers
  const handleAddToWatchlist = async (playerId: number) => {
    if (!currentUserId || !selectedLeague || watchlistActionInProgress) return;

    // Trouver les informations du joueur
    const allPlayerArrays = [...players, ...myPlayers, ...scratchPlayers, ...myScratchPlayers, ...injuredPlayers, ...myInjuredPlayers];
    const allGoalieArrays = [...goalies, ...myGoalies, ...scratchGoalies, ...myScratchGoalies, ...injuredGoalies, ...myInjuredGoalies];
    const player = allPlayerArrays.find(p => p.playerid === playerId) || allGoalieArrays.find(g => g.playerid === playerId);
    
    if (!player) {
      toast.error('Joueur introuvable');
      return;
    }

    try {
      setWatchlistActionInProgress(true);
      const result = await addToWatchlist(
        selectedLeague.id, 
        Number(currentUserId), 
        playerId,
        player.skaterfullname || player.goaliefullname || '',
        player.positioncode || 'G',
        !!player.goaliefullname, // isGoalie
        player.salary,
        player.teamabbrevs || ''
      );
      
      if (result.success) {
        setWatchlistPlayerIds(prev => new Set([...prev, playerId]));
        toast.success(
          language === 'fr' 
            ? 'Joueur ajouté à la liste de surveillance' 
            : 'Player added to watchlist'
        );
      } else {
        throw new Error(result.error?.message || 'Erreur lors de l\'ajout à la liste de surveillance');
      }
    } catch (error) {
      console.error('Error adding to watchlist:', error);
      toast.error(
        language === 'fr' 
          ? 'Erreur lors de l\'ajout à la liste de surveillance'
          : 'Error adding to watchlist'
      );
    } finally {
      setWatchlistActionInProgress(false);
    }
  };

  const handleRemoveFromWatchlist = async (playerId: number) => {
    if (!currentUserId || !selectedLeague || watchlistActionInProgress) return;

    try {
      setWatchlistActionInProgress(true);
      const result = await removeFromWatchlist(selectedLeague.id, Number(currentUserId), playerId);
      
      if (result.success) {
        setWatchlistPlayerIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(playerId);
          return newSet;
        });
        toast.success(
          language === 'fr' 
            ? 'Joueur retiré de la liste de surveillance' 
            : 'Player removed from watchlist'
        );
      } else {
        throw new Error(result.error?.message || 'Erreur lors de la suppression de la liste de surveillance');
      }
    } catch (error) {
      console.error('Error removing from watchlist:', error);
      toast.error(
        language === 'fr' 
          ? 'Erreur lors de la suppression de la liste de surveillance'
          : 'Error removing from watchlist'
      );
    } finally {
      setWatchlistActionInProgress(false);
    }
  };

  // Auto-scroll on mobile to highlighted table row (any tab)
  useEffect(() => {
    if (!highlightPlayerId) return;
    if (typeof window === 'undefined' || window.innerWidth >= 768) return; // mobile only

    let attempts = 0;
    const maxAttempts = 12; // ~1.8s with 150ms interval

    const tryScroll = () => {
      attempts++;
      const target = document.querySelector('tr.table-warning');
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (attempts < maxAttempts) {
        setTimeout(tryScroll, 150);
      }
    };

    // Initial small delay to allow first paint
    const start = setTimeout(tryScroll, 150);
    return () => clearTimeout(start);
  }, [highlightPlayerId, activeTab, playerSearchTerm, players, goalies, scratchPlayers, scratchGoalies, injuredPlayers, injuredGoalies]);

  useEffect(() => {
    if (!selectedLeague) {
      navigate('/dashboard');
      return;
    }

    async function loadSettings() {
      try {
        const { data, error } = await getLeagueSettings(selectedLeague!.id);
        if (error) throw error;
        setLeagueSettings(data);
        
        // Charger les limites max depuis league_settings
        const { data: settingsData, error: settingsError } = await supabase
          .from('league_settings')
          .select('max_signing, max_demotion')
          .eq('league_id', selectedLeague!.id)
          .single();
        
        if (settingsError && settingsError.code !== 'PGRST116') {
          console.error('Error loading max signing/demotion:', settingsError);
        } else if (settingsData) {
          // Respecter une valeur explicite de 0 pour max_signing / max_demotion
          setMaxSigning(settingsData.max_signing != null ? Number(settingsData.max_signing) : 15);
          setMaxDemotion(settingsData.max_demotion != null ? Number(settingsData.max_demotion) : 10);
        }
      } catch (error) {
        console.error('Error loading league settings:', error);
        toast.error('Failed to load league settings');
      }
    }

    loadSettings();
  }, [selectedLeague, navigate]);

  useEffect(() => {
    if (!selectedLeague || !selectedTeamId) return;

    async function loadSalaryCaps() {
      try {
        // Fetch main club salary cap
        const { data: mainCapData, error: mainCapError } = await supabase
          .from('get_salary_cap_per_team')
          .select('salary_cap')
          .eq('league_id', selectedLeague!.id)
          .eq('user_id', selectedTeamId)
          .eq('isnhl', true)
          .single();

        if (mainCapError && mainCapError.code !== 'PGRST116') throw mainCapError;
        setCurrentSalaryCap(mainCapData?.salary_cap || 0);

        // Fetch reserve team salary cap
        const { data: reserveCapData, error: reserveCapError } = await supabase
          .from('get_salary_cap_per_team')
          .select('salary_cap')
          .eq('league_id', selectedLeague!.id)
          .eq('user_id', selectedTeamId)
          .eq('isnhl', false)
          .single();

        if (reserveCapError && reserveCapError.code !== 'PGRST116') throw reserveCapError;
        setCurrentReserveSalaryCap(reserveCapData?.salary_cap || 0);
        
        // Charger les compteurs de signatures et rétrogradations
        const { data: userLeagueData, error: userLeagueError } = await supabase
          .from('leagues_users')
          .select('number_of_sigining_used, number_of_demotion_used')
          .eq('league_id', selectedLeague!.id)
          .eq('user_id', selectedTeamId)
          .single();
        
        if (userLeagueError && userLeagueError.code !== 'PGRST116') {
          console.error('Error loading user league counters:', userLeagueError);
        } else if (userLeagueData) {
          setSigningUsed(userLeagueData.number_of_sigining_used != null ? Number(userLeagueData.number_of_sigining_used) : 0);
          setDemotionUsed(userLeagueData.number_of_demotion_used != null ? Number(userLeagueData.number_of_demotion_used) : 0);
        }
      } catch (error) {
        console.error('Error loading salary caps:', error);
      }
    }

    loadSalaryCaps();
  }, [selectedLeague, selectedTeamId]);

  // Note: we currently don't display my-team salary caps in the comparison view.

  useEffect(() => {
    if (!selectedLeague || !currentUserId || modeLoading) return;

    async function loadTeams() {
      try {
        // Pass 0 to include all teams, including the current user's team
        const { data, error } = await getUserByLeaguSaufUserConnecter(
          selectedLeague!.id,
          0,
          isStandardFlexible
        );

        if (error) throw error;
        setTeams(data || []);

        // Load watchlist player IDs
        const watchlistResult = await getWatchlistPlayerIds(selectedLeague!.id, Number(currentUserId));
        if (watchlistResult.data) {
          setWatchlistPlayerIds(new Set(watchlistResult.data));
        }
      } catch (error) {
        console.error('Error loading teams:', error);
        toast.error('Erreur lors du chargement des équipes');
      } finally {
        setLoading(false);
      }
    }

    loadTeams();
  }, [selectedLeague, currentUserId, modeLoading, isStandardFlexible]);

  // Nouvelle logique pour sélectionner automatiquement l'équipe basée sur l'URL
  useEffect(() => {
    if (!userId || teams.length === 0 || modeLoading) return;

    if (userId && teams.length > 0) {
      const targetUserId = parseInt(userId, 10);
      const targetTeam = visibleTeams.find(team => team.users_user_id === targetUserId);
      
      if (targetTeam) {
        setSelectedTeam(targetTeam.username);
        setSelectedTeamId(targetTeam.users_user_id);
      } else {
        setSelectedTeam('');
        setSelectedTeamId(null);
        navigate('/other-teams', { replace: true });
      }
    }
  }, [userId, teams, visibleTeams, modeLoading, navigate]);

  useEffect(() => {
    if (!selectedLeague || !selectedTeamId) {
      setPlayers([]);
      setGoalies([]);
      setInjuredPlayers([]);
      setInjuredGoalies([]);
      setScratchPlayers([]);
      setScratchGoalies([]);
      return;
    }

    async function loadTeamData() {
      try {
        setLoading(true);
        // Fetch players and goalies
        const [playersResponse, goaliesResponse] = await Promise.all([
          getMyteamPlayer(selectedLeague!.id, selectedTeamId as number),
          getMyTeamGoalies(selectedLeague!.id, selectedTeamId as number)
        ]);

        if (playersResponse.error) throw playersResponse.error;
        if (goaliesResponse.error) throw goaliesResponse.error;
        
        // Fetch injury data
        const { data: injuryData, error: injuryError } = await supabase
          .from('injury_player')
          .select('playerid, description');

        if (injuryError) throw injuryError;

        setInjuryDescriptions(injuryData?.map(injury => ({
          playerid: injury.playerid,
          description: injury.description || 'Joueur blessé'
        })) || []);

        // Fetch injured status from league_players_user
        const { data: injuredStatusData, error: injuredStatusError } = await supabase
          .from('league_players_user')
          .select('player_id, is_injured')
          .eq('league_id', selectedLeague!.id)
          .eq('user_id', selectedTeamId)
          .eq('is_injured', true);

        if (injuredStatusError) throw injuredStatusError;

        // Fetch scratch players status from league_players_user
        const { data: scratchStatusData, error: scratchStatusError } = await supabase
          .from('league_players_user')
          .select('player_id, is_scratch')
          .eq('league_id', selectedLeague!.id)
          .eq('user_id', selectedTeamId)
          .eq('is_scratch', true)
          .eq('isnhl', true);

        if (scratchStatusError) throw scratchStatusError;

        console.log('🔍 Debug OtherTeams - Scratch Status Data:', scratchStatusData);
        console.log('🔍 Debug OtherTeams - Number of scratch entries:', scratchStatusData?.length || 0);

        // Create sets of player IDs for fast lookup
        const injuredPlayerIds = new Set(injuredStatusData?.map(p => p.player_id) || []);
        const scratchPlayerIds = new Set(scratchStatusData?.map(p => p.player_id) || []);

        // Split players and goalies by status
        const allPlayers = playersResponse.data || [];
        const mainAndReservePlayers = [];
        const injured = [];
        const scratch = [];

        for (const player of allPlayers) {
          if (injuredPlayerIds.has(player.playerid)) {
            injured.push(player);
          } else if (scratchPlayerIds.has(player.playerid) && player.isnhl) {
            // Seuls les joueurs NHL (isnhl = true) qui sont scratch
            scratch.push(player);
          } else {
            // Only include in main/reserve if not scratch (même si isnhl est true)
            if (!scratchPlayerIds.has(player.playerid)) {
              mainAndReservePlayers.push(player);
            }
          }
        }

        setPlayers(mainAndReservePlayers);
        setInjuredPlayers(injured);
        setScratchPlayers(scratch);

        console.log('🔍 Debug OtherTeams - All Players count:', allPlayers.length);
        console.log('🔍 Debug OtherTeams - Main/Reserve Players count:', mainAndReservePlayers.length);
        console.log('🔍 Debug OtherTeams - Injured Players count:', injured.length);
        console.log('🔍 Debug OtherTeams - Scratch Players count:', scratch.length);
        console.log('🔍 Debug OtherTeams - Scratch Players:', scratch);

        // Split goalies by status
        const allGoalies = goaliesResponse.data || [];
        const mainAndReserveGoalies = [];
        const injuredGoaliesList = [];
        const scratchGoaliesList = [];

        for (const goalie of allGoalies) {
          if (injuredPlayerIds.has(goalie.playerid)) {
            injuredGoaliesList.push(goalie);
          } else if (scratchPlayerIds.has(goalie.playerid) && goalie.isnhl) {
            // Seuls les gardiens NHL (isnhl = true) qui sont scratch
            scratchGoaliesList.push(goalie);
          } else {
            // Only include in main/reserve if not scratch (même si isnhl est true)
            if (!scratchPlayerIds.has(goalie.playerid)) {
              mainAndReserveGoalies.push(goalie);
            }
          }
        }

        setGoalies(mainAndReserveGoalies);
        setInjuredGoalies(injuredGoaliesList);
        setScratchGoalies(scratchGoaliesList);

        console.log('🔍 Debug OtherTeams - All Goalies count:', allGoalies.length);
        console.log('🔍 Debug OtherTeams - Main/Reserve Goalies count:', mainAndReserveGoalies.length);
        console.log('🔍 Debug OtherTeams - Injured Goalies count:', injuredGoaliesList.length);
        console.log('🔍 Debug OtherTeams - Scratch Goalies count:', scratchGoaliesList.length);
        console.log('🔍 Debug OtherTeams - Scratch Goalies:', scratchGoaliesList);
        
        console.log('🎯 Summary - Players/Goalies are properly separated by status and the scratch section should now be visible!');

        // Fetch draft picks
        const { data: draftPicksData, error: draftPicksError } = await getUserDraftPicks(
          selectedLeague!.id,
          selectedTeamId as number
        );

        if (draftPicksError) {
          console.error('Error loading draft picks:', draftPicksError);
        } else {
          setDraftPicks(draftPicksData || []);
        }

      } catch (error) {
        console.error('Error loading team data:', error);
        toast.error('Erreur lors du chargement des données de l\'équipe');
      } finally {
        setLoading(false);
      }
    }

    loadTeamData();
  }, [selectedLeague, selectedTeamId]);

  // Load my team data (for desktop left column)
  useEffect(() => {
    if (!selectedLeague || !currentUserId) {
      setMyPlayers([]);
      setMyGoalies([]);
      setMyInjuredPlayers([]);
      setMyInjuredGoalies([]);
      setMyScratchPlayers([]);
      setMyScratchGoalies([]);
      return;
    }

    async function loadMyTeamData() {
      try {
        setMyLoading(true);
        const [playersResponse, goaliesResponse] = await Promise.all([
          getMyteamPlayer(selectedLeague!.id, Number(currentUserId)),
          getMyTeamGoalies(selectedLeague!.id, Number(currentUserId))
        ]);

        if (playersResponse.error) throw playersResponse.error;
        if (goaliesResponse.error) throw goaliesResponse.error;

        // Injury descriptions (shared)
        const { data: injuryData, error: injuryError } = await supabase
          .from('injury_player')
          .select('playerid, description');
        if (injuryError) throw injuryError;
        setInjuryDescriptions(injuryData?.map(injury => ({
          playerid: injury.playerid,
          description: injury.description || 'Joueur blessé'
        })) || []);

        // Injured and scratch statuses for my team
        const { data: myInjuredStatusData, error: myInjuredStatusError } = await supabase
          .from('league_players_user')
          .select('player_id, is_injured')
          .eq('league_id', selectedLeague!.id)
          .eq('user_id', Number(currentUserId))
          .eq('is_injured', true);
        if (myInjuredStatusError) throw myInjuredStatusError;

        const { data: myScratchStatusData, error: myScratchStatusError } = await supabase
          .from('league_players_user')
          .select('player_id, is_scratch')
          .eq('league_id', selectedLeague!.id)
          .eq('user_id', Number(currentUserId))
          .eq('is_scratch', true)
          .eq('isnhl', true);
        if (myScratchStatusError) throw myScratchStatusError;

        const injuredIds = new Set(myInjuredStatusData?.map(p => p.player_id) || []);
        const scratchIds = new Set(myScratchStatusData?.map(p => p.player_id) || []);

        const allPlayers = playersResponse.data || [];
        const mainAndReservePlayers: any[] = [];
        const injured: any[] = [];
        const scratch: any[] = [];
        for (const p of allPlayers) {
          if (injuredIds.has(p.playerid)) {
            injured.push(p);
          } else if (scratchIds.has(p.playerid) && p.isnhl) {
            scratch.push(p);
          } else if (!scratchIds.has(p.playerid)) {
            mainAndReservePlayers.push(p);
          }
        }
        setMyPlayers(mainAndReservePlayers);
        setMyInjuredPlayers(injured);
        setMyScratchPlayers(scratch);

        const allGoalies = goaliesResponse.data || [];
        const mainAndReserveGoalies: any[] = [];
        const injuredGoaliesList: any[] = [];
        const scratchGoaliesList: any[] = [];
        for (const g of allGoalies) {
          if (injuredIds.has(g.playerid)) {
            injuredGoaliesList.push(g);
          } else if (scratchIds.has(g.playerid) && g.isnhl) {
            scratchGoaliesList.push(g);
          } else if (!scratchIds.has(g.playerid)) {
            mainAndReserveGoalies.push(g);
          }
        }
        setMyGoalies(mainAndReserveGoalies);
        setMyInjuredGoalies(injuredGoaliesList);
        setMyScratchGoalies(scratchGoaliesList);
      } catch (error) {
        console.error('Error loading my team data:', error);
      } finally {
        setMyLoading(false);
      }
    }
    loadMyTeamData();
  }, [selectedLeague, currentUserId]);

  const handleTeamChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const teamId = parseInt(e.target.value);
    const team = teams.find(t => t.users_user_id === teamId);
    setSelectedTeam(team ? team.username : '');
    setSelectedTeamId(teamId || null);
    // Reset player search when switching team manually
    setPlayerSearchTerm('');
    setPlayerSearchResults([]);
    setHighlightPlayerId(null);
    // Mettre à jour l'URL pour préserver l'équipe sélectionnée lors du retour arrière
    if (teamId) {
      navigate(`/other-teams/${teamId}`, { replace: true });
    }
  };

  // Recherche par joueur (auto complete)
  useEffect(() => {
    let active = true;
    async function runSearch() {
      if (!selectedLeague) return;
      const term = playerSearchTerm.trim();
      if (term.length < 3) { // éviter requêtes inutiles
        setPlayerSearchResults([]);
        return;
      }
      try {
        setSearchLoading(true);
        const { data, error } = await searchLeaguePlayers(selectedLeague.id, term);
        if (!active) return;
        if (error) throw error;
        setPlayerSearchResults(data || []);
      } catch (err:any) {
        if (active) {
          console.error('Erreur recherche joueur:', err);
        }
      } finally {
        if (active) setSearchLoading(false);
      }
    }
    runSearch();
    return () => { active = false; };
  }, [playerSearchTerm, selectedLeague]);

  // Team search (client-side) - only for standard_flexible mode
  useEffect(() => {
    let active = true;
    if (!isStandardFlexible) {
      setTeamSearchResults([]);
      return;
    }

    const term = teamSearchTerm.trim();
    if (term.length < 3) {
      setTeamSearchResults([]);
      return;
    }

    setTeamSearchLoading(true);
    const handler = setTimeout(() => {
      if (!active) return;
      const lower = term.toLowerCase();
      const results = visibleTeams.filter(t => (t.username || '').toLowerCase().includes(lower));
      // Limiter à 10 résultats pour éviter de surcharger l'UI
      setTeamSearchResults(results.slice(0, 10));
      setTeamSearchLoading(false);
    }, 200);

    return () => {
      active = false;
      clearTimeout(handler);
      setTeamSearchLoading(false);
    };
  }, [teamSearchTerm, visibleTeams, isStandardFlexible]);

  const handleSelectTeamResult = (teamId: number) => {
    const team = visibleTeams.find(t => t.users_user_id === teamId);
    if (!team) return;
    setSelectedTeamId(teamId);
    setSelectedTeam(team.username);
    setTeamSearchResults([]);
    setTeamSearchTerm('');
    // Mettre à jour l'URL pour préserver l'équipe sélectionnée lors du retour arrière
    navigate(`/other-teams/${teamId}`, { replace: true });
  };

  const handleSelectPlayerResult = (playerId: number) => {
    const player = playerSearchResults.find(p => p.playerid === playerId);
    if (!player) return;
    // Trouver équipe (user) et charger
    const team = visibleTeams.find(t => t.users_user_id === player.user_id);
    if (isStandardFlexible && !team) return;
    setSelectedTeamId(player.user_id);
    setSelectedTeam(team ? team.username : '');
    // Effacer résultats (garder term pour info)
    setPlayerSearchResults([]);
    // Toujours aller à la vue d'ensemble pour garantir la visibilité (main / réserve / blessé / scratch)
    setActiveSection('MAIN'); // sans impact en overview mais garde un état cohérent
    setHighlightPlayerId(player.playerid);
    // Mettre à jour l'URL pour préserver l'équipe sélectionnée lors du retour arrière
    navigate(`/other-teams/${player.user_id}`, { replace: true });
  };

  // Parent sorting handlers removed (handled within table components)

  const filteredPlayers = players.filter((player) => {
    if (activeSection === 'INJURED') {
      return false; // Injured players are shown separately
    }
    
    // Ne pas inclure les joueurs scratch même s'ils ont isnhl = true
    if (player.isnhl !== (activeSection === 'MAIN')) return false;
    
    switch (selectedPosition) {
      case 'FORWARD':
        return ['C', 'L', 'R'].includes(player.positioncode);
      case 'DEFENSE':
        return player.positioncode === 'D';
      case 'C':
      case 'L':
      case 'R':
      case 'D':
        return player.positioncode === selectedPosition;
      default:
        return true;
    }
  });

  const filteredGoalies = goalies.filter(
    (goalie) => {
      if (activeSection === 'INJURED') {
        return false; // Injured goalies are shown separately
      }
      return goalie.isnhl === (activeSection === 'MAIN');
    }
  );

  const filteredInjuredPlayers = injuredPlayers.filter((player) => {
    if (activeSection !== 'INJURED') {
      return false;
    }
    
    switch (selectedPosition) {
      case 'FORWARD':
        return ['C', 'L', 'R'].includes(player.positioncode);
      case 'DEFENSE':
        return player.positioncode === 'D';
      case 'C':
      case 'L':
      case 'R':
      case 'D':
        return player.positioncode === selectedPosition;
      default:
        return true;
    }
  });

  const filteredInjuredGoalies = injuredGoalies.filter(
    () => activeSection === 'INJURED'
  );

  const filteredScratchPlayers = scratchPlayers.filter((player) => {
    if (activeSection !== 'SCRATCH') {
      return false;
    }
    
    switch (selectedPosition) {
      case 'FORWARD':
        return ['C', 'L', 'R'].includes(player.positioncode);
      case 'DEFENSE':
        return player.positioncode === 'D';
      case 'C':
      case 'L':
      case 'R':
      case 'D':
        return player.positioncode === selectedPosition;
      default:
        return true;
    }
  });

  const filteredScratchGoalies = scratchGoalies.filter(
    () => activeSection === 'SCRATCH'
  );

  const getPlayersToShow = () => {
    switch (activeSection) {
      case 'INJURED':
        return filteredInjuredPlayers;
      case 'SCRATCH':
        return filteredScratchPlayers;
      default:
        return filteredPlayers;
    }
  };

  const getGoaliesToShow = () => {
    switch (activeSection) {
      case 'INJURED':
        return filteredInjuredGoalies;
      case 'SCRATCH':
        return filteredScratchGoalies;
      default:
        return filteredGoalies;
    }
  };

  const sortedPlayers = [...getPlayersToShow()].sort((a, b) => {
    const aValue = a[sortConfig.field];
    const bValue = b[sortConfig.field];
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return bValue.localeCompare(aValue); // desc
    }
    return (bValue as number) - (aValue as number);
  });

  const sortedGoalies = [...getGoaliesToShow()].sort((a, b) => {
    const aValue = a[goalieSortConfig.field];
    const bValue = b[goalieSortConfig.field];
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return bValue.localeCompare(aValue); // desc
    }
    return (bValue as number) - (aValue as number);
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Removed getInjuryLimit (injury usage card relocated / omitted in this view)

  // ======================= OVERVIEW DATA (Vue d'ensemble) =======================
  // Partition main vs reserves (players/goalies) excluding scratch & injured already split earlier
  const mainPlayersOverviewRaw = players.filter(p => p.isnhl);
  const reservePlayersOverviewRaw = players.filter(p => !p.isnhl);
  const mainGoaliesOverviewRaw = goalies.filter(g => g.isnhl);
  const reserveGoaliesOverviewRaw = goalies.filter(g => !g.isnhl);

  // Grouping helpers (Forwards vs Defense) for Overview only
  const isForward = (p: any) => ['C', 'L', 'R'].includes(p.positioncode);
  const isDefense = (p: any) => p.positioncode === 'D';

  // Sorting helpers reuse existing sortConfig / goalieSortConfig
  const sortPlayers = (list: any[]) => [...list].sort((a,b) => {
    const aValue = a[sortConfig.field];
    const bValue = b[sortConfig.field];
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return bValue.localeCompare(aValue); // fixed desc
    }
    return (bValue as number) - (aValue as number); // fixed desc
  });
  const sortGoalies = (list: any[]) => [...list].sort((a,b) => {
    const aValue = a[goalieSortConfig.field];
    const bValue = b[goalieSortConfig.field];
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return bValue.localeCompare(aValue); // fixed desc
    }
    return (bValue as number) - (aValue as number); // fixed desc
  });

  // Selected Team grouped lists
  const sortedMainForwardsOverview = sortPlayers(mainPlayersOverviewRaw.filter(isForward));
  const sortedMainDefenseOverview = sortPlayers(mainPlayersOverviewRaw.filter(isDefense));
  const sortedReserveForwardsOverview = sortPlayers(reservePlayersOverviewRaw.filter(isForward));
  const sortedReserveDefenseOverview = sortPlayers(reservePlayersOverviewRaw.filter(isDefense));
  const sortedScratchForwardsOverview = sortPlayers(scratchPlayers.filter(isForward));
  const sortedScratchDefenseOverview = sortPlayers(scratchPlayers.filter(isDefense));
  const sortedInjuredForwardsOverview = sortPlayers(injuredPlayers.filter(isForward));
  const sortedInjuredDefenseOverview = sortPlayers(injuredPlayers.filter(isDefense));

  const sortedMainGoaliesOverview = sortGoalies(mainGoaliesOverviewRaw);
  const sortedReserveGoaliesOverview = sortGoalies(reserveGoaliesOverviewRaw);
  const sortedScratchGoaliesOverview = sortGoalies(scratchGoalies);
  const sortedInjuredGoaliesOverview = sortGoalies(injuredGoalies);

  // ======================= OVERVIEW DATA FOR MY TEAM =======================
  const myMainPlayersOverviewRaw = myPlayers.filter(p => p.isnhl);
  const myReservePlayersOverviewRaw = myPlayers.filter(p => !p.isnhl);
  const myMainGoaliesOverviewRaw = myGoalies.filter(g => g.isnhl);
  const myReserveGoaliesOverviewRaw = myGoalies.filter(g => !g.isnhl);

  // My Team grouped lists
  const mySortedMainForwardsOverview = sortPlayers(myMainPlayersOverviewRaw.filter(isForward));
  const mySortedMainDefenseOverview = sortPlayers(myMainPlayersOverviewRaw.filter(isDefense));
  const mySortedReserveForwardsOverview = sortPlayers(myReservePlayersOverviewRaw.filter(isForward));
  const mySortedReserveDefenseOverview = sortPlayers(myReservePlayersOverviewRaw.filter(isDefense));
  const mySortedScratchForwardsOverview = sortPlayers(myScratchPlayers.filter(isForward));
  const mySortedScratchDefenseOverview = sortPlayers(myScratchPlayers.filter(isDefense));
  const mySortedInjuredForwardsOverview = sortPlayers(myInjuredPlayers.filter(isForward));
  const mySortedInjuredDefenseOverview = sortPlayers(myInjuredPlayers.filter(isDefense));

  const mySortedMainGoaliesOverview = sortGoalies(myMainGoaliesOverviewRaw);
  const mySortedReserveGoaliesOverview = sortGoalies(myReserveGoaliesOverviewRaw);
  const mySortedScratchGoaliesOverview = sortGoalies(myScratchGoalies);
  const mySortedInjuredGoaliesOverview = sortGoalies(myInjuredGoalies);

  if (!selectedLeague) {
    navigate('/dashboard');
    return null;
  }

  return (
    <div id="otherteams-page" className={`card shadow-lg border-0${isEmbed ? ' other-teams-embed' : ''}`} style={{ overflow: 'visible' }}>
      <div className="card-body p-3 p-md-5" style={{ overflow: 'visible' }}>
        <OfficeBackButton visible={isBaseStandardUi} />
        <EmbedBackButton />
        <div className="mb-4">
          <label className="form-label">Sélectionner une équipe</label>
          <select
            className="form-select"
            value={selectedTeamId || ''}
            onChange={handleTeamChange}
          >
            <option value="">Choisir une équipe</option>
            {visibleTeams.map((team) => (
              <option key={team.users_user_id} value={team.users_user_id}>
                {team.username}
              </option>
            ))}
          </select>
        </div>

        {/* Mini-recherche: équipe en mode standard_flexible, sinon recherche joueur */}
        {isStandardFlexible ? (
          <div className="mb-4 position-relative">
            <label className="form-label">Rechercher une équipe (≥ 3 lettres)</label>
            <input
              type="text"
              className="form-control"
              placeholder="Nom de l'équipe..."
              value={teamSearchTerm}
              onChange={(e) => setTeamSearchTerm(e.target.value)}
            />
            {teamSearchLoading && (
              <div className="small text-muted mt-1">Recherche...</div>
            )}
            {teamSearchResults.length > 0 && (
              <div className="list-group position-absolute w-100 shadow" style={{ zIndex: 20, maxHeight: '260px', overflowY: 'auto' }}>
                {teamSearchResults.map(team => (
                  <button
                    key={team.users_user_id}
                    type="button"
                    className="list-group-item list-group-item-action"
                    onClick={() => handleSelectTeamResult(team.users_user_id)}
                  >
                    {team.username}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="mb-4 position-relative">
            <label className="form-label">Rechercher un joueur (≥ 3 lettres)</label>
            <input
              type="text"
              className="form-control"
              placeholder="Nom du joueur..."
              value={playerSearchTerm}
              onChange={(e) => setPlayerSearchTerm(e.target.value)}
            />
            {searchLoading && (
              <div className="small text-muted mt-1">Recherche...</div>
            )}
            {playerSearchResults.length > 0 && (
              <div className="list-group position-absolute w-100 shadow" style={{ zIndex: 20, maxHeight: '260px', overflowY: 'auto' }}>
                {playerSearchResults.map(res => (
                  <button
                    key={res.playerid}
                    type="button"
                    className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                    onClick={() => handleSelectPlayerResult(res.playerid)}
                  >
                    <span>{res.fullname}</span>
                    <span className="badge bg-secondary">{res.isGoalie ? 'G' : 'P'}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

  {loading && (
          <div className="text-center p-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Chargement...</span>
            </div>
          </div>
        )}

  {!loading && selectedTeam && leagueSettings && (
          <>
            {selectedLeague && selectedTeamId && currentUserId && Number(selectedTeamId) !== Number(currentUserId) && (
              <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-2 mb-3">
                <div className="fw-semibold">
                  {language === 'fr' ? 'Équipe affichée' : 'Selected team'}: {selectedTeam}
                </div>
                <PoolerFollowButton
                  leagueId={selectedLeague.id}
                  currentUserId={Number(currentUserId)}
                  targetUserId={selectedTeamId}
                />
              </div>
            )}
            {((activeSection === 'MAIN' && leagueSettings.salary_cap) || 
              (activeSection === 'RESERVES' && leagueSettings.salary_cap_reserve)) && (
              <div className="mb-2">
                <button
                  type="button"
                  className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-between"
                  onClick={() => setShowSalaryInfo((prev) => !prev)}
                >
                  <span className="d-flex align-items-center">
                    <Info size={18} className="me-2" />
                    {language === 'fr' ? 'Information Salariale' : 'Salary Information'}
                  </span>
                  <span>{showSalaryInfo ? '−' : '+'}</span>
                </button>
              </div>
            )}

            {showSalaryInfo && ((activeSection === 'MAIN' && leagueSettings.salary_cap) || 
              (activeSection === 'RESERVES' && leagueSettings.salary_cap_reserve)) && (
              <div className="card mb-4 border-0 bg-light">
                <div className="card-body">
                  <div className="d-flex align-items-center mb-3">
                    <Info size={20} className="text-primary me-2" />
                    <h5 className="mb-0">Information Salariale Équipe recherché</h5>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="card h-100">
                        <div className="card-body">
                          <h6 className="card-title d-flex align-items-center">
                            <DollarSign size={18} className="me-2 text-primary" />
                            Utilisation du Plafond Salarial
                          </h6>
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <span>Actuel:</span>
                            <span className="fw-bold">
                              {formatCurrency(
                                activeSection === 'MAIN'
                                  ? (currentSalaryCap || 0)
                                  : (currentReserveSalaryCap || 0)
                              )}
                            </span>
                          </div>
                          <div className="d-flex align-items-center justify-content-between">
                            <span>Maximum:</span>
                            <span>
                              {formatCurrency(
                                activeSection === 'MAIN'
                                  ? leagueSettings.max_salary_cap
                                  : leagueSettings.max_salary_cap_reserve
                              )}
                            </span>
                          </div>
                          <div className="progress mt-2" style={{ height: '8px' }}>
                            <div
                              className={`progress-bar ${
                                activeSection === 'MAIN'
                                  ? (currentSalaryCap! > leagueSettings.max_salary_cap ? 'bg-danger' : 'bg-success')
                                  : (currentReserveSalaryCap! > leagueSettings.max_salary_cap_reserve ? 'bg-danger' : 'bg-success')
                              }`}
                              role="progressbar"
                              style={{
                                width: `${Math.min(
                                  activeSection === 'MAIN'
                                    ? ((currentSalaryCap || 0) / leagueSettings.max_salary_cap) * 100
                                    : ((currentReserveSalaryCap || 0) / leagueSettings.max_salary_cap_reserve) * 100,
                                  100
                                )}%`
                              }}
                              aria-valuenow={activeSection === 'MAIN' ? (currentSalaryCap || 0) : (currentReserveSalaryCap || 0)}
                              aria-valuemin={0}
                              aria-valuemax={activeSection === 'MAIN' ? leagueSettings.max_salary_cap : leagueSettings.max_salary_cap_reserve}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="mb-2">
              <button
                type="button"
                className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-between"
                onClick={() => setShowPlayerMovements((prev) => !prev)}
              >
                <span className="d-flex align-items-center">
                  <Info size={18} className="me-2" />
                  {language === 'fr' ? 'Mouvements de joueurs' : 'Player Movements'}
                </span>
                <span>{showPlayerMovements ? '−' : '+'}</span>
              </button>
            </div>

            {/* Section Compteurs de Signatures et Rétrogradations */}
            {showPlayerMovements && (
              <div className="card mb-4 border-0 bg-light">
                <div className="card-body">
                  <div className="d-flex align-items-center mb-3">
                    <Info size={20} className="text-primary me-2" />
                    <h5 className="mb-0">{language === 'fr' ? 'Mouvements de joueurs' : 'Player Movements'}</h5>
                  </div>
                  <div className="row g-3">
                    {/* Signatures */}
                    <div className="col-md-6">
                      <div className="card h-100 border-success">
                        <div className="card-body">
                          <h6 className="card-title d-flex align-items-center text-success mb-3">
                            <Plus size={18} className="me-2" />
                            {language === 'fr' ? 'Signatures' : 'Signings'}
                          </h6>
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <span>{language === 'fr' ? 'Utilisées:' : 'Used:'}</span>
                            <span className="fw-bold fs-4 text-success">{signingUsed}</span>
                          </div>
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <span>{language === 'fr' ? 'Maximum:' : 'Maximum:'}</span>
                            <span className="text-muted">{maxSigning}</span>
                          </div>
                          <div className="d-flex align-items-center justify-content-between mb-3">
                            <span className="fw-bold">{language === 'fr' ? 'Restantes:' : 'Remaining:'}</span>
                            <span className={`fw-bold fs-5 ${(maxSigning - signingUsed) <= 2 ? 'text-danger' : 'text-primary'}`}>
                              {maxSigning - signingUsed}
                            </span>
                          </div>
                          <div className="progress" style={{ height: '10px' }}>
                            <div
                              className={`progress-bar ${signingUsed >= maxSigning ? 'bg-danger' : signingUsed >= maxSigning * 0.8 ? 'bg-warning' : 'bg-success'}`}
                              role="progressbar"
                              style={{ width: `${Math.min((signingUsed / maxSigning) * 100, 100)}%` }}
                              aria-valuenow={signingUsed}
                              aria-valuemin={0}
                              aria-valuemax={maxSigning}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {!hideDemotionCard && (
                      <div className="col-md-6">
                        <div className="card h-100 border-warning">
                          <div className="card-body">
                            <h6 className="card-title d-flex align-items-center text-warning mb-3">
                              <ArrowLeft size={18} className="me-2" />
                              {language === 'fr' ? 'Rétrogradations' : 'Demotions'}
                            </h6>
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <span>{language === 'fr' ? 'Utilisées:' : 'Used:'}</span>
                              <span className="fw-bold fs-4 text-warning">{demotionUsed}</span>
                            </div>
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <span>{language === 'fr' ? 'Maximum:' : 'Maximum:'}</span>
                              <span className="text-muted">{maxDemotion}</span>
                            </div>
                            <div className="d-flex align-items-center justify-content-between mb-3">
                              <span className="fw-bold">{language === 'fr' ? 'Restantes:' : 'Remaining:'}</span>
                              <span className={`fw-bold fs-5 ${(maxDemotion - demotionUsed) <= 2 ? 'text-danger' : 'text-primary'}`}>
                                {maxDemotion - demotionUsed}
                              </span>
                            </div>
                            <div className="progress" style={{ height: '10px' }}>
                              <div
                                className={`progress-bar ${demotionUsed >= maxDemotion ? 'bg-danger' : demotionUsed >= maxDemotion * 0.8 ? 'bg-warning' : 'bg-info'}`}
                                role="progressbar"
                                style={{ width: `${Math.min((demotionUsed / maxDemotion) * 100, 100)}%` }}
                                aria-valuenow={demotionUsed}
                                aria-valuemin={0}
                                aria-valuemax={maxDemotion}
                              ></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Navigation masquée pour maximiser l'espace; vue d'ensemble uniquement */}

            {activeSection === 'INJURED' && (
              <div className="alert alert-info mb-4">
                <div className="d-flex align-items-center">
                  <Bandage size={18} className="text-danger me-2" />
                  <span>
                    {language === 'fr'
                      ? 'Les joueurs blessés sont indiqués par une icône rouge. Cliquez sur l\'icône pour voir la description de la blessure.'
                      : 'Injured players are indicated by a red icon. Click on the icon to view the injury description.'}
                  </span>
                </div>
              </div>
            )}

            {activeSection === 'SCRATCH' && (
              <div className="alert alert-warning mb-4">
                <div className="d-flex align-items-center">
                  <UserX size={18} className="text-warning me-2" />
                  <span>
                    {language === 'fr'
                      ? 'Les joueurs scratch sont des joueurs NHL qui ne sont pas dans la formation active mais qui comptent pour le plafond salarial.'
                      : 'Scratch players are NHL players who are not in the active lineup but still count against the salary cap.'}
                  </span>
                </div>
              </div>
            )}

            {/* Onglets masqués pour maximiser l'espace; vue d'ensemble uniquement */}

            {/* Desktop side-by-side comparison (md and up) */}
            {activeTab === 'overview' && (
              <div className="overview-sections">
                {/* Desktop: two columns; Mobile: original single list below via d-md-none */}
                <div className="d-none d-md-block">
                  <div className="row g-3">
                    {/* Left: My Team */}
                    <div className="col-md-6">
                      <h4 className="mb-3 d-flex align-items-center gap-2">
                        {(() => {
                          const myLogo = visibleTeams.find(t => t.users_user_id === Number(currentUserId))?.nhl_team_abbrev;
                          return myLogo ? <TeamLogo abbrev={myLogo} size={40} /> : null;
                        })()}
                        {(visibleTeams.find(t => t.users_user_id === Number(currentUserId))?.username) || (language==='fr' ? 'Mon équipe' : 'My Team')}
                      </h4>
                      {myLoading ? (
                        <div className="text-center p-4"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                      ) : (
                        <>
                          {/* My - Main Forwards */}
                          {mySortedMainForwardsOverview.length > 0 && (
                            <div className="mb-4">
                              <h6 className="mb-2 d-flex align-items-center"><Users size={18} className="me-2 text-primary" />{language==='fr'?'Club Principal - Attaquants':'Main Club - Forwards'}<span className="badge bg-primary ms-2">{mySortedMainForwardsOverview.length}</span></h6>
                              <OtherTeamsTable
                                players={mySortedMainForwardsOverview}
                                language={language}
                                injuredPlayers={injuryDescriptions}
                                onViewDetails={handleViewPlayerDetails}
                              watchlistPlayerIds={watchlistPlayerIds}
                              onAddToWatchlist={handleAddToWatchlist}
                              onRemoveFromWatchlist={handleRemoveFromWatchlist}
                              watchlistActionInProgress={watchlistActionInProgress}
                              />
                            </div>
                          )}
                          {/* My - Main Defense */}
                          {mySortedMainDefenseOverview.length > 0 && (
                            <div className="mb-4">
                              <h6 className="mb-2 d-flex align-items-center"><Users size={18} className="me-2 text-primary" />{language==='fr'?'Club Principal - Défenseurs':'Main Club - Defensemen'}<span className="badge bg-primary ms-2">{mySortedMainDefenseOverview.length}</span></h6>
                              <OtherTeamsTable
                                players={mySortedMainDefenseOverview}
                                language={language}
                                injuredPlayers={injuryDescriptions}
                                onViewDetails={handleViewPlayerDetails}
                              watchlistPlayerIds={watchlistPlayerIds}
                              onAddToWatchlist={handleAddToWatchlist}
                              onRemoveFromWatchlist={handleRemoveFromWatchlist}
                              watchlistActionInProgress={watchlistActionInProgress}
                              />
                            </div>
                          )}
                          {/* My - Main Goalies */}
                          {mySortedMainGoaliesOverview.length > 0 && (
                            <div className="mb-4">
                              <h6 className="mb-2 d-flex align-items-center"><Users size={18} className="me-2 text-primary" />{language==='fr'?'Club Principal - Gardiens':'Main Club - Goalies'}<span className="badge bg-primary ms-2">{mySortedMainGoaliesOverview.length}</span></h6>
                              <OtherTeamsGoalieTable
                                goalies={mySortedMainGoaliesOverview}
                                language={language}
                                injuredPlayers={injuryDescriptions}
                                onViewDetails={handleViewPlayerDetails}
                              watchlistPlayerIds={watchlistPlayerIds}
                              onAddToWatchlist={handleAddToWatchlist}
                              onRemoveFromWatchlist={handleRemoveFromWatchlist}
                              watchlistActionInProgress={watchlistActionInProgress}
                              />
                            </div>
                          )}
                          {/* My - Scratch Forwards */}
                          {mySortedScratchForwardsOverview.length > 0 && (
                            <div className="mb-4">
                              <h6 className="mb-2 d-flex align-items-center"><Users size={18} className="me-2 text-warning" />Scratch - Attaquants<span className="badge bg-warning ms-2">{mySortedScratchForwardsOverview.length}</span></h6>
                              <OtherTeamsTable
                                players={mySortedScratchForwardsOverview}
                                language={language}
                                injuredPlayers={injuryDescriptions}
                                onViewDetails={handleViewPlayerDetails}
                              watchlistPlayerIds={watchlistPlayerIds}
                              onAddToWatchlist={handleAddToWatchlist}
                              onRemoveFromWatchlist={handleRemoveFromWatchlist}
                              watchlistActionInProgress={watchlistActionInProgress}
                              />
                            </div>
                          )}
                          {/* My - Scratch Defense */}
                          {mySortedScratchDefenseOverview.length > 0 && (
                            <div className="mb-4">
                              <h6 className="mb-2 d-flex align-items-center"><Users size={18} className="me-2 text-warning" />Scratch - Défenseurs<span className="badge bg-warning ms-2">{mySortedScratchDefenseOverview.length}</span></h6>
                              <OtherTeamsTable
                                players={mySortedScratchDefenseOverview}
                                language={language}
                                injuredPlayers={injuryDescriptions}
                                onViewDetails={handleViewPlayerDetails}
                              watchlistPlayerIds={watchlistPlayerIds}
                              onAddToWatchlist={handleAddToWatchlist}
                              onRemoveFromWatchlist={handleRemoveFromWatchlist}
                              watchlistActionInProgress={watchlistActionInProgress}
                              />
                            </div>
                          )}
                          {/* My - Scratch Goalies */}
                          {mySortedScratchGoaliesOverview.length > 0 && (
                            <div className="mb-4">
                              <h6 className="mb-2 d-flex align-items-center"><Users size={18} className="me-2 text-warning" />{language==='fr'?'Scratch Gardiens':'Scratch Goalies'}<span className="badge bg-warning ms-2">{mySortedScratchGoaliesOverview.length}</span></h6>
                              <OtherTeamsGoalieTable
                                goalies={mySortedScratchGoaliesOverview}
                                language={language}
                                injuredPlayers={injuryDescriptions}
                                onViewDetails={handleViewPlayerDetails}
                              watchlistPlayerIds={watchlistPlayerIds}
                              onAddToWatchlist={handleAddToWatchlist}
                              onRemoveFromWatchlist={handleRemoveFromWatchlist}
                              watchlistActionInProgress={watchlistActionInProgress}
                              />
                            </div>
                          )}
                          {/* My - Reserves Forwards */}
                          {mySortedReserveForwardsOverview.length > 0 && (
                            <div className="mb-4">
                              <h6 className="mb-2 d-flex align-items-center"><Users size={18} className="me-2 text-info" />{language==='fr'?'Réserves - Attaquants':'Reserves - Forwards'}<span className="badge bg-info ms-2">{mySortedReserveForwardsOverview.length}</span></h6>
                              <OtherTeamsTable
                                players={mySortedReserveForwardsOverview}
                                language={language}
                                injuredPlayers={injuryDescriptions}
                                onViewDetails={handleViewPlayerDetails}
                              watchlistPlayerIds={watchlistPlayerIds}
                              onAddToWatchlist={handleAddToWatchlist}
                              onRemoveFromWatchlist={handleRemoveFromWatchlist}
                              watchlistActionInProgress={watchlistActionInProgress}
                              />
                            </div>
                          )}
                          {/* My - Reserves Defense */}
                          {mySortedReserveDefenseOverview.length > 0 && (
                            <div className="mb-4">
                              <h6 className="mb-2 d-flex align-items-center"><Users size={18} className="me-2 text-info" />{language==='fr'?'Réserves - Défenseurs':'Reserves - Defensemen'}<span className="badge bg-info ms-2">{mySortedReserveDefenseOverview.length}</span></h6>
                              <OtherTeamsTable
                                players={mySortedReserveDefenseOverview}
                                language={language}
                                injuredPlayers={injuryDescriptions}
                                onViewDetails={handleViewPlayerDetails}
                              watchlistPlayerIds={watchlistPlayerIds}
                              onAddToWatchlist={handleAddToWatchlist}
                              onRemoveFromWatchlist={handleRemoveFromWatchlist}
                              watchlistActionInProgress={watchlistActionInProgress}
                              />
                            </div>
                          )}
                          {/* My - Reserves Goalies */}
                          {mySortedReserveGoaliesOverview.length > 0 && (
                            <div className="mb-4">
                              <h6 className="mb-2 d-flex align-items-center"><Users size={18} className="me-2 text-info" />{language==='fr'?'Réserves - Gardiens':'Reserves - Goalies'}<span className="badge bg-info ms-2">{mySortedReserveGoaliesOverview.length}</span></h6>
                              <OtherTeamsGoalieTable
                                goalies={mySortedReserveGoaliesOverview}
                                language={language}
                                injuredPlayers={injuryDescriptions}
                                onViewDetails={handleViewPlayerDetails}
                              watchlistPlayerIds={watchlistPlayerIds}
                              onAddToWatchlist={handleAddToWatchlist}
                              onRemoveFromWatchlist={handleRemoveFromWatchlist}
                              watchlistActionInProgress={watchlistActionInProgress}
                              />
                            </div>
                          )}
                          {/* My - Injured Forwards */}
                          {mySortedInjuredForwardsOverview.length > 0 && (
                            <div className="mb-4">
                              <h6 className="mb-2 d-flex align-items-center"><Bandage size={18} className="me-2 text-danger" />{language==='fr'?'Liste des blessés - Attaquants':'Injured List - Forwards'}<span className="badge bg-danger ms-2">{mySortedInjuredForwardsOverview.length}</span></h6>
                              <OtherTeamsTable
                                players={mySortedInjuredForwardsOverview}
                                language={language}
                                injuredPlayers={injuryDescriptions}
                                onViewDetails={handleViewPlayerDetails}
                              watchlistPlayerIds={watchlistPlayerIds}
                              onAddToWatchlist={handleAddToWatchlist}
                              onRemoveFromWatchlist={handleRemoveFromWatchlist}
                              watchlistActionInProgress={watchlistActionInProgress}
                              />
                            </div>
                          )}
                          {/* My - Injured Defense */}
                          {mySortedInjuredDefenseOverview.length > 0 && (
                            <div className="mb-4">
                              <h6 className="mb-2 d-flex align-items-center"><Bandage size={18} className="me-2 text-danger" />{language==='fr'?'Liste des blessés - Défenseurs':'Injured List - Defensemen'}<span className="badge bg-danger ms-2">{mySortedInjuredDefenseOverview.length}</span></h6>
                              <OtherTeamsTable
                                players={mySortedInjuredDefenseOverview}
                                language={language}
                                injuredPlayers={injuryDescriptions}
                                onViewDetails={handleViewPlayerDetails}
                              watchlistPlayerIds={watchlistPlayerIds}
                              onAddToWatchlist={handleAddToWatchlist}
                              onRemoveFromWatchlist={handleRemoveFromWatchlist}
                              watchlistActionInProgress={watchlistActionInProgress}
                              />
                            </div>
                          )}
                          {/* My - Injured Goalies */}
                          {mySortedInjuredGoaliesOverview.length > 0 && (
                            <div className="mb-4">
                              <h6 className="mb-2 d-flex align-items-center"><Bandage size={18} className="me-2 text-danger" />{language==='fr'?'Liste des blessés - Gardiens':'Injured List - Goalies'}<span className="badge bg-danger ms-2">{mySortedInjuredGoaliesOverview.length}</span></h6>
                              <OtherTeamsGoalieTable
                                goalies={mySortedInjuredGoaliesOverview}
                                language={language}
                                injuredPlayers={injuryDescriptions}
                                onViewDetails={handleViewPlayerDetails}
                              watchlistPlayerIds={watchlistPlayerIds}
                              onAddToWatchlist={handleAddToWatchlist}
                              onRemoveFromWatchlist={handleRemoveFromWatchlist}
                              watchlistActionInProgress={watchlistActionInProgress}
                              />
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    {/* Right: Selected Team (existing lists) - desktop tables only */}
                    <div className="col-md-6">
                      <h4 className="mb-3 d-flex align-items-center gap-2">
                        {(() => {
                          const opponentLogo = visibleTeams.find(t => t.users_user_id === selectedTeamId)?.nhl_team_abbrev;
                          return opponentLogo ? <TeamLogo abbrev={opponentLogo} size={40} /> : null;
                        })()}
                        {selectedTeam}
                      </h4>
                      {/* Selected - Main Forwards */}
                      {sortedMainForwardsOverview.length > 0 && (
                        <div className="mb-4">
                          <h6 className="mb-2 d-flex align-items-center"><Users size={18} className="me-2 text-primary" />{language==='fr'?'Club Principal - Attaquants':'Main Club - Forwards'}<span className="badge bg-primary ms-2">{sortedMainForwardsOverview.length}</span></h6>
                          <OtherTeamsTable
                            players={sortedMainForwardsOverview}
                            language={language}
                            injuredPlayers={injuryDescriptions}
                            highlightPlayerId={highlightPlayerId ?? undefined}
                            highlightTerm={highlightPlayerId ? playerSearchTerm : ''}
                            onViewDetails={handleViewPlayerDetails}
                          watchlistPlayerIds={watchlistPlayerIds}
                          onAddToWatchlist={handleAddToWatchlist}
                          onRemoveFromWatchlist={handleRemoveFromWatchlist}
                          watchlistActionInProgress={watchlistActionInProgress}
                          />
                        </div>
                      )}
                      {/* Selected - Main Defense */}
                      {sortedMainDefenseOverview.length > 0 && (
                        <div className="mb-4">
                          <h6 className="mb-2 d-flex align-items-center"><Users size={18} className="me-2 text-primary" />{language==='fr'?'Club Principal - Défenseurs':'Main Club - Defensemen'}<span className="badge bg-primary ms-2">{sortedMainDefenseOverview.length}</span></h6>
                          <OtherTeamsTable
                            players={sortedMainDefenseOverview}
                            language={language}
                            injuredPlayers={injuryDescriptions}
                            highlightPlayerId={highlightPlayerId ?? undefined}
                            highlightTerm={highlightPlayerId ? playerSearchTerm : ''}
                            onViewDetails={handleViewPlayerDetails}
                          watchlistPlayerIds={watchlistPlayerIds}
                          onAddToWatchlist={handleAddToWatchlist}
                          onRemoveFromWatchlist={handleRemoveFromWatchlist}
                          watchlistActionInProgress={watchlistActionInProgress}
                          />
                        </div>
                      )}
                      {sortedMainGoaliesOverview.length > 0 && (
                        <div className="mb-4">
                          <h6 className="mb-2 d-flex align-items-center"><Users size={18} className="me-2 text-primary" />{language==='fr'?'Club Principal - Gardiens':'Main Club - Goalies'}<span className="badge bg-primary ms-2">{sortedMainGoaliesOverview.length}</span></h6>
                          <OtherTeamsGoalieTable
                            goalies={sortedMainGoaliesOverview}
                            language={language}
                            injuredPlayers={injuryDescriptions}
                            highlightPlayerId={highlightPlayerId ?? undefined}
                            highlightTerm={highlightPlayerId ? playerSearchTerm : ''}
                            onViewDetails={handleViewPlayerDetails}
                          watchlistPlayerIds={watchlistPlayerIds}
                          onAddToWatchlist={handleAddToWatchlist}
                          onRemoveFromWatchlist={handleRemoveFromWatchlist}
                          watchlistActionInProgress={watchlistActionInProgress}
                          />
                        </div>
                      )}
                      {sortedScratchForwardsOverview.length > 0 && (
                        <div className="mb-4">
                          <h6 className="mb-2 d-flex align-items-center"><Users size={18} className="me-2 text-warning" />Scratch - Attaquants<span className="badge bg-warning ms-2">{sortedScratchForwardsOverview.length}</span></h6>
                          <OtherTeamsTable
                            players={sortedScratchForwardsOverview}
                            language={language}
                            injuredPlayers={injuryDescriptions}
                            highlightPlayerId={highlightPlayerId ?? undefined}
                            highlightTerm={highlightPlayerId ? playerSearchTerm : ''}
                            onViewDetails={handleViewPlayerDetails}
                          watchlistPlayerIds={watchlistPlayerIds}
                          onAddToWatchlist={handleAddToWatchlist}
                          onRemoveFromWatchlist={handleRemoveFromWatchlist}
                          watchlistActionInProgress={watchlistActionInProgress}
                          />
                        </div>
                      )}
                      {sortedScratchDefenseOverview.length > 0 && (
                        <div className="mb-4">
                          <h6 className="mb-2 d-flex align-items-center"><Users size={18} className="me-2 text-warning" />Scratch - Défenseurs<span className="badge bg-warning ms-2">{sortedScratchDefenseOverview.length}</span></h6>
                          <OtherTeamsTable
                            players={sortedScratchDefenseOverview}
                            language={language}
                            injuredPlayers={injuryDescriptions}
                            highlightPlayerId={highlightPlayerId ?? undefined}
                            highlightTerm={highlightPlayerId ? playerSearchTerm : ''}
                            onViewDetails={handleViewPlayerDetails}
                          watchlistPlayerIds={watchlistPlayerIds}
                          onAddToWatchlist={handleAddToWatchlist}
                          onRemoveFromWatchlist={handleRemoveFromWatchlist}
                          watchlistActionInProgress={watchlistActionInProgress}
                          />
                        </div>
                      )}
                      {sortedScratchGoaliesOverview.length > 0 && (
                        <div className="mb-4">
                          <h6 className="mb-2 d-flex align-items-center"><Users size={18} className="me-2 text-warning" />{language==='fr'?'Scratch Gardiens':'Scratch Goalies'}<span className="badge bg-warning ms-2">{sortedScratchGoaliesOverview.length}</span></h6>
                          <OtherTeamsGoalieTable
                            goalies={sortedScratchGoaliesOverview}
                            language={language}
                            injuredPlayers={injuryDescriptions}
                            highlightPlayerId={highlightPlayerId ?? undefined}
                            highlightTerm={highlightPlayerId ? playerSearchTerm : ''}
                            onViewDetails={handleViewPlayerDetails}
                          watchlistPlayerIds={watchlistPlayerIds}
                          onAddToWatchlist={handleAddToWatchlist}
                          onRemoveFromWatchlist={handleRemoveFromWatchlist}
                          watchlistActionInProgress={watchlistActionInProgress}
                          />
                        </div>
                      )}
                      {sortedReserveForwardsOverview.length > 0 && (
                        <div className="mb-4">
                          <h6 className="mb-2 d-flex align-items-center"><Users size={18} className="me-2 text-info" />{language==='fr'?'Réserves - Attaquants':'Reserves - Forwards'}<span className="badge bg-info ms-2">{sortedReserveForwardsOverview.length}</span></h6>
                          <OtherTeamsTable
                            players={sortedReserveForwardsOverview}
                            language={language}
                            injuredPlayers={injuryDescriptions}
                            highlightPlayerId={highlightPlayerId ?? undefined}
                            highlightTerm={highlightPlayerId ? playerSearchTerm : ''}
                            onViewDetails={handleViewPlayerDetails}
                          watchlistPlayerIds={watchlistPlayerIds}
                          onAddToWatchlist={handleAddToWatchlist}
                          onRemoveFromWatchlist={handleRemoveFromWatchlist}
                          watchlistActionInProgress={watchlistActionInProgress}
                          />
                        </div>
                      )}
                      {sortedReserveDefenseOverview.length > 0 && (
                        <div className="mb-4">
                          <h6 className="mb-2 d-flex align-items-center"><Users size={18} className="me-2 text-info" />{language==='fr'?'Réserves - Défenseurs':'Reserves - Defensemen'}<span className="badge bg-info ms-2">{sortedReserveDefenseOverview.length}</span></h6>
                          <OtherTeamsTable
                            players={sortedReserveDefenseOverview}
                            language={language}
                            injuredPlayers={injuryDescriptions}
                            highlightPlayerId={highlightPlayerId ?? undefined}
                            highlightTerm={highlightPlayerId ? playerSearchTerm : ''}
                            onViewDetails={handleViewPlayerDetails}
                          watchlistPlayerIds={watchlistPlayerIds}
                          onAddToWatchlist={handleAddToWatchlist}
                          onRemoveFromWatchlist={handleRemoveFromWatchlist}
                          watchlistActionInProgress={watchlistActionInProgress}
                          />
                        </div>
                      )}
                      {sortedReserveGoaliesOverview.length > 0 && (
                        <div className="mb-4">
                          <h6 className="mb-2 d-flex align-items-center"><Users size={18} className="me-2 text-info" />{language==='fr'?'Réserves - Gardiens':'Reserves - Goalies'}<span className="badge bg-info ms-2">{sortedReserveGoaliesOverview.length}</span></h6>
                          <OtherTeamsGoalieTable
                            goalies={sortedReserveGoaliesOverview}
                            language={language}
                            injuredPlayers={injuryDescriptions}
                            highlightPlayerId={highlightPlayerId ?? undefined}
                            highlightTerm={highlightPlayerId ? playerSearchTerm : ''}
                            onViewDetails={handleViewPlayerDetails}
                          watchlistPlayerIds={watchlistPlayerIds}
                          onAddToWatchlist={handleAddToWatchlist}
                          onRemoveFromWatchlist={handleRemoveFromWatchlist}
                          watchlistActionInProgress={watchlistActionInProgress}
                          />
                        </div>
                      )}
                      {sortedInjuredForwardsOverview.length > 0 && (
                        <div className="mb-4">
                          <h6 className="mb-2 d-flex align-items-center"><Bandage size={18} className="me-2 text-danger" />{language==='fr'?'Liste des blessés - Attaquants':'Injured List - Forwards'}<span className="badge bg-danger ms-2">{sortedInjuredForwardsOverview.length}</span></h6>
                          <OtherTeamsTable
                            players={sortedInjuredForwardsOverview}
                            language={language}
                            injuredPlayers={injuryDescriptions}
                            highlightPlayerId={highlightPlayerId ?? undefined}
                            highlightTerm={highlightPlayerId ? playerSearchTerm : ''}
                            onViewDetails={handleViewPlayerDetails}
                          watchlistPlayerIds={watchlistPlayerIds}
                          onAddToWatchlist={handleAddToWatchlist}
                          onRemoveFromWatchlist={handleRemoveFromWatchlist}
                          watchlistActionInProgress={watchlistActionInProgress}
                          />
                        </div>
                      )}
                      {sortedInjuredDefenseOverview.length > 0 && (
                        <div className="mb-4">
                          <h6 className="mb-2 d-flex align-items-center"><Bandage size={18} className="me-2 text-danger" />{language==='fr'?'Liste des blessés - Défenseurs':'Injured List - Defensemen'}<span className="badge bg-danger ms-2">{sortedInjuredDefenseOverview.length}</span></h6>
                          <OtherTeamsTable
                            players={sortedInjuredDefenseOverview}
                            language={language}
                            injuredPlayers={injuryDescriptions}
                            highlightPlayerId={highlightPlayerId ?? undefined}
                            highlightTerm={highlightPlayerId ? playerSearchTerm : ''}
                            onViewDetails={handleViewPlayerDetails}
                          watchlistPlayerIds={watchlistPlayerIds}
                          onAddToWatchlist={handleAddToWatchlist}
                          onRemoveFromWatchlist={handleRemoveFromWatchlist}
                          watchlistActionInProgress={watchlistActionInProgress}
                          />
                        </div>
                      )}
                      {sortedInjuredGoaliesOverview.length > 0 && (
                        <div className="mb-4">
                          <h6 className="mb-2 d-flex align-items-center"><Bandage size={18} className="me-2 text-danger" />{language==='fr'?'Liste des blessés - Gardiens':'Injured List - Goalies'}<span className="badge bg-danger ms-2">{sortedInjuredGoaliesOverview.length}</span></h6>
                          <OtherTeamsGoalieTable
                            goalies={sortedInjuredGoaliesOverview}
                            language={language}
                            injuredPlayers={injuryDescriptions}
                            highlightPlayerId={highlightPlayerId ?? undefined}
                            highlightTerm={highlightPlayerId ? playerSearchTerm : ''}
                            onViewDetails={handleViewPlayerDetails}
                          watchlistPlayerIds={watchlistPlayerIds}
                          onAddToWatchlist={handleAddToWatchlist}
                          onRemoveFromWatchlist={handleRemoveFromWatchlist}
                          watchlistActionInProgress={watchlistActionInProgress}
                          />
                        </div>
                      )}
                      {/* Choix au repêchage Section */}
                      {draftPicks.length > 0 && (
                        <div className="mb-4">
                          <h6 className="mb-2 d-flex align-items-center">
                            <span className="me-2">📋</span>
                            {language === 'fr' ? 'Choix au repêchage' : 'Draft Picks'}
                            <span className="badge bg-warning text-dark ms-2">{draftPicks.length}</span>
                          </h6>
                          <div className="row g-3">
                            {draftPicks.map((pick) => (
                              <div key={pick.id} className="col-md-6 col-lg-4">
                                <div className="card border-warning h-100">
                                  <div className="card-body">
                                    <div className="d-flex align-items-center mb-2">
                                      <div className="bg-warning rounded-circle d-flex align-items-center justify-content-center me-3" 
                                           style={{ width: '40px', height: '40px', minWidth: '40px' }}>
                                        <span className="fw-bold text-dark">📋</span>
                                      </div>
                                      <div className="flex-grow-1">
                                        <h6 className="mb-1 fw-bold text-warning">{pick.description}</h6>
                                        <small className="text-muted">
                                          {language === 'fr' ? 'Choix de repêchage' : 'Draft Pick'}
                                        </small>
                                      </div>
                                    </div>
                                    
                                    {pick.originalUserName && (
                                      <div className="mt-2">
                                        <small className="text-muted">
                                          <strong>Propriétaire original:</strong> {pick.originalUserName}
                                        </small>
                                      </div>
                                    )}
                                    
                                    <div className="mt-2">
                                      <span className="badge bg-warning text-dark">
                                        Ronde {pick.roundNumber}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {/* Mobile: keep existing single-column cards and tables */}
                <div className="d-md-none">
                  {/* Mobile: use the same table components (CSS makes them compact rows) */}
                  {sortedMainForwardsOverview.length > 0 && (
                    <div className="mb-4">
                      <h5 className="mb-3 d-flex align-items-center">
                        <Users size={20} className="me-2 text-primary" />
                        {language === 'fr' ? 'Club Principal - Attaquants' : 'Main Club - Forwards'}
                        <span className="badge bg-primary ms-2">{sortedMainForwardsOverview.length}</span>
                      </h5>
                      <OtherTeamsTable
                        players={sortedMainForwardsOverview}
                        language={language}
                        injuredPlayers={injuryDescriptions}
                        highlightPlayerId={highlightPlayerId ?? undefined}
                        highlightTerm={highlightPlayerId ? playerSearchTerm : ''}
                        onViewDetails={handleViewPlayerDetails}
                        watchlistPlayerIds={watchlistPlayerIds}
                        onAddToWatchlist={handleAddToWatchlist}
                        onRemoveFromWatchlist={handleRemoveFromWatchlist}
                        watchlistActionInProgress={watchlistActionInProgress}
                      />
                    </div>
                  )}

                  {sortedMainDefenseOverview.length > 0 && (
                    <div className="mb-4">
                      <h5 className="mb-3 d-flex align-items-center">
                        <Users size={20} className="me-2 text-primary" />
                        {language === 'fr' ? 'Club Principal - Défenseurs' : 'Main Club - Defensemen'}
                        <span className="badge bg-primary ms-2">{sortedMainDefenseOverview.length}</span>
                      </h5>
                      <OtherTeamsTable
                        players={sortedMainDefenseOverview}
                        language={language}
                        injuredPlayers={injuryDescriptions}
                        highlightPlayerId={highlightPlayerId ?? undefined}
                        highlightTerm={highlightPlayerId ? playerSearchTerm : ''}
                        onViewDetails={handleViewPlayerDetails}
                        watchlistPlayerIds={watchlistPlayerIds}
                        onAddToWatchlist={handleAddToWatchlist}
                        onRemoveFromWatchlist={handleRemoveFromWatchlist}
                        watchlistActionInProgress={watchlistActionInProgress}
                      />
                    </div>
                  )}

                  {sortedMainGoaliesOverview.length > 0 && (
                    <div className="mb-4">
                      <h5 className="mb-3 d-flex align-items-center">
                        <Users size={20} className="me-2 text-primary" />
                        {language === 'fr' ? 'Club Principal - Gardiens' : 'Main Club - Goalies'}
                        <span className="badge bg-primary ms-2">{sortedMainGoaliesOverview.length}</span>
                      </h5>
                      <OtherTeamsGoalieTable
                        goalies={sortedMainGoaliesOverview}
                        language={language}
                        injuredPlayers={injuryDescriptions}
                        highlightPlayerId={highlightPlayerId ?? undefined}
                        highlightTerm={highlightPlayerId ? playerSearchTerm : ''}
                        onViewDetails={handleViewPlayerDetails}
                        watchlistPlayerIds={watchlistPlayerIds}
                        onAddToWatchlist={handleAddToWatchlist}
                        onRemoveFromWatchlist={handleRemoveFromWatchlist}
                        watchlistActionInProgress={watchlistActionInProgress}
                      />
                    </div>
                  )}

                  {sortedScratchForwardsOverview.length > 0 && (
                    <div className="mb-4">
                      <h5 className="mb-3 d-flex align-items-center">
                        <Users size={20} className="me-2 text-warning" />
                        Scratch - Attaquants
                        <span className="badge bg-warning ms-2">{sortedScratchForwardsOverview.length}</span>
                      </h5>
                      <OtherTeamsTable
                        players={sortedScratchForwardsOverview}
                        language={language}
                        injuredPlayers={injuryDescriptions}
                        highlightPlayerId={highlightPlayerId ?? undefined}
                        highlightTerm={highlightPlayerId ? playerSearchTerm : ''}
                        onViewDetails={handleViewPlayerDetails}
                        watchlistPlayerIds={watchlistPlayerIds}
                        onAddToWatchlist={handleAddToWatchlist}
                        onRemoveFromWatchlist={handleRemoveFromWatchlist}
                        watchlistActionInProgress={watchlistActionInProgress}
                      />
                    </div>
                  )}

                  {sortedScratchDefenseOverview.length > 0 && (
                    <div className="mb-4">
                      <h5 className="mb-3 d-flex align-items-center">
                        <Users size={20} className="me-2 text-warning" />
                        Scratch - Défenseurs
                        <span className="badge bg-warning ms-2">{sortedScratchDefenseOverview.length}</span>
                      </h5>
                      <OtherTeamsTable
                        players={sortedScratchDefenseOverview}
                        language={language}
                        injuredPlayers={injuryDescriptions}
                        highlightPlayerId={highlightPlayerId ?? undefined}
                        highlightTerm={highlightPlayerId ? playerSearchTerm : ''}
                        onViewDetails={handleViewPlayerDetails}
                        watchlistPlayerIds={watchlistPlayerIds}
                        onAddToWatchlist={handleAddToWatchlist}
                        onRemoveFromWatchlist={handleRemoveFromWatchlist}
                        watchlistActionInProgress={watchlistActionInProgress}
                      />
                    </div>
                  )}

                  {sortedScratchGoaliesOverview.length > 0 && (
                    <div className="mb-4">
                      <h5 className="mb-3 d-flex align-items-center">
                        <Users size={20} className="me-2 text-warning" />
                        {language === 'fr' ? 'Scratch Gardiens' : 'Scratch Goalies'}
                        <span className="badge bg-warning ms-2">{sortedScratchGoaliesOverview.length}</span>
                      </h5>
                      <OtherTeamsGoalieTable
                        goalies={sortedScratchGoaliesOverview}
                        language={language}
                        injuredPlayers={injuryDescriptions}
                        highlightPlayerId={highlightPlayerId ?? undefined}
                        highlightTerm={highlightPlayerId ? playerSearchTerm : ''}
                        onViewDetails={handleViewPlayerDetails}
                        watchlistPlayerIds={watchlistPlayerIds}
                        onAddToWatchlist={handleAddToWatchlist}
                        onRemoveFromWatchlist={handleRemoveFromWatchlist}
                        watchlistActionInProgress={watchlistActionInProgress}
                      />
                    </div>
                  )}

                  {sortedReserveForwardsOverview.length > 0 && (
                    <div className="mb-4">
                      <h5 className="mb-3 d-flex align-items-center">
                        <Users size={20} className="me-2 text-info" />
                        {language === 'fr' ? 'Réserves - Attaquants' : 'Reserves - Forwards'}
                        <span className="badge bg-info ms-2">{sortedReserveForwardsOverview.length}</span>
                      </h5>
                      <OtherTeamsTable
                        players={sortedReserveForwardsOverview}
                        language={language}
                        injuredPlayers={injuryDescriptions}
                        highlightPlayerId={highlightPlayerId ?? undefined}
                        highlightTerm={highlightPlayerId ? playerSearchTerm : ''}
                        onViewDetails={handleViewPlayerDetails}
                        watchlistPlayerIds={watchlistPlayerIds}
                        onAddToWatchlist={handleAddToWatchlist}
                        onRemoveFromWatchlist={handleRemoveFromWatchlist}
                        watchlistActionInProgress={watchlistActionInProgress}
                      />
                    </div>
                  )}

                  {sortedReserveDefenseOverview.length > 0 && (
                    <div className="mb-4">
                      <h5 className="mb-3 d-flex align-items-center">
                        <Users size={20} className="me-2 text-info" />
                        {language === 'fr' ? 'Réserves - Défenseurs' : 'Reserves - Defensemen'}
                        <span className="badge bg-info ms-2">{sortedReserveDefenseOverview.length}</span>
                      </h5>
                      <OtherTeamsTable
                        players={sortedReserveDefenseOverview}
                        language={language}
                        injuredPlayers={injuryDescriptions}
                        highlightPlayerId={highlightPlayerId ?? undefined}
                        highlightTerm={highlightPlayerId ? playerSearchTerm : ''}
                        onViewDetails={handleViewPlayerDetails}
                        watchlistPlayerIds={watchlistPlayerIds}
                        onAddToWatchlist={handleAddToWatchlist}
                        onRemoveFromWatchlist={handleRemoveFromWatchlist}
                        watchlistActionInProgress={watchlistActionInProgress}
                      />
                    </div>
                  )}

                  {sortedReserveGoaliesOverview.length > 0 && (
                    <div className="mb-4">
                      <h5 className="mb-3 d-flex align-items-center">
                        <Users size={20} className="me-2 text-info" />
                        {language === 'fr' ? 'Réserves - Gardiens' : 'Reserves - Goalies'}
                        <span className="badge bg-info ms-2">{sortedReserveGoaliesOverview.length}</span>
                      </h5>
                      <OtherTeamsGoalieTable
                        goalies={sortedReserveGoaliesOverview}
                        language={language}
                        injuredPlayers={injuryDescriptions}
                        highlightPlayerId={highlightPlayerId ?? undefined}
                        highlightTerm={highlightPlayerId ? playerSearchTerm : ''}
                        onViewDetails={handleViewPlayerDetails}
                        watchlistPlayerIds={watchlistPlayerIds}
                        onAddToWatchlist={handleAddToWatchlist}
                        onRemoveFromWatchlist={handleRemoveFromWatchlist}
                        watchlistActionInProgress={watchlistActionInProgress}
                      />
                    </div>
                  )}

                  {sortedInjuredForwardsOverview.length > 0 && (
                    <div className="mb-4">
                      <h5 className="mb-3 d-flex align-items-center">
                        <Bandage size={20} className="me-2 text-danger" />
                        {language === 'fr' ? 'Liste des blessés - Attaquants' : 'Injured List - Forwards'}
                        <span className="badge bg-danger ms-2">{sortedInjuredForwardsOverview.length}</span>
                      </h5>
                      <OtherTeamsTable
                        players={sortedInjuredForwardsOverview}
                        language={language}
                        injuredPlayers={injuryDescriptions}
                        highlightPlayerId={highlightPlayerId ?? undefined}
                        highlightTerm={highlightPlayerId ? playerSearchTerm : ''}
                        onViewDetails={handleViewPlayerDetails}
                        watchlistPlayerIds={watchlistPlayerIds}
                        onAddToWatchlist={handleAddToWatchlist}
                        onRemoveFromWatchlist={handleRemoveFromWatchlist}
                        watchlistActionInProgress={watchlistActionInProgress}
                      />
                    </div>
                  )}

                  {sortedInjuredDefenseOverview.length > 0 && (
                    <div className="mb-4">
                      <h5 className="mb-3 d-flex align-items-center">
                        <Bandage size={20} className="me-2 text-danger" />
                        {language === 'fr' ? 'Liste des blessés - Défenseurs' : 'Injured List - Defensemen'}
                        <span className="badge bg-danger ms-2">{sortedInjuredDefenseOverview.length}</span>
                      </h5>
                      <OtherTeamsTable
                        players={sortedInjuredDefenseOverview}
                        language={language}
                        injuredPlayers={injuryDescriptions}
                        highlightPlayerId={highlightPlayerId ?? undefined}
                        highlightTerm={highlightPlayerId ? playerSearchTerm : ''}
                        onViewDetails={handleViewPlayerDetails}
                        watchlistPlayerIds={watchlistPlayerIds}
                        onAddToWatchlist={handleAddToWatchlist}
                        onRemoveFromWatchlist={handleRemoveFromWatchlist}
                        watchlistActionInProgress={watchlistActionInProgress}
                      />
                    </div>
                  )}

                  {sortedInjuredGoaliesOverview.length > 0 && (
                    <div className="mb-4">
                      <h5 className="mb-3 d-flex align-items-center">
                        <Bandage size={20} className="me-2 text-danger" />
                        {language === 'fr' ? 'Liste des blessés - Gardiens' : 'Injured List - Goalies'}
                        <span className="badge bg-danger ms-2">{sortedInjuredGoaliesOverview.length}</span>
                      </h5>
                      <OtherTeamsGoalieTable
                        goalies={sortedInjuredGoaliesOverview}
                        language={language}
                        injuredPlayers={injuryDescriptions}
                        highlightPlayerId={highlightPlayerId ?? undefined}
                        highlightTerm={highlightPlayerId ? playerSearchTerm : ''}
                        onViewDetails={handleViewPlayerDetails}
                        watchlistPlayerIds={watchlistPlayerIds}
                        onAddToWatchlist={handleAddToWatchlist}
                        onRemoveFromWatchlist={handleRemoveFromWatchlist}
                        watchlistActionInProgress={watchlistActionInProgress}
                      />
                    </div>
                  )}
                  {/* Choix au repêchage Section - Mobile */}
                  {draftPicks.length > 0 && (
                    <div className="mb-4">
                      <h5 className="mb-3 d-flex align-items-center">
                        <span className="me-2">📋</span>
                        {language === 'fr' ? 'Choix au repêchage' : 'Draft Picks'}
                        <span className="badge bg-warning text-dark ms-2">{draftPicks.length}</span>
                      </h5>
                      
                      <div className="row g-3">
                        {draftPicks.map((pick) => (
                          <div key={pick.id} className="col-12">
                            <div className="card border-warning h-100">
                              <div className="card-body">
                                <div className="d-flex align-items-center mb-2">
                                  <div className="bg-warning rounded-circle d-flex align-items-center justify-content-center me-3" 
                                       style={{ width: '40px', height: '40px', minWidth: '40px' }}>
                                    <span className="fw-bold text-dark">📋</span>
                                  </div>
                                  <div className="flex-grow-1">
                                    <h6 className="mb-1 fw-bold text-warning">{pick.description}</h6>
                                    <small className="text-muted">
                                      {language === 'fr' ? 'Choix de repêchage' : 'Draft Pick'}
                                    </small>
                                  </div>
                                </div>
                                
                                {pick.originalUserName && (
                                  <div className="mt-2">
                                    <small className="text-muted">
                                      <strong>Propriétaire original:</strong> {pick.originalUserName}
                                    </small>
                                  </div>
                                )}
                                
                                <div className="mt-2">
                                  <span className="badge bg-warning text-dark">
                                    Ronde {pick.roundNumber}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {(mainPlayersOverviewRaw.length === 0 && mainGoaliesOverviewRaw.length === 0 &&
                    scratchPlayers.length === 0 && sortedScratchGoaliesOverview.length === 0 &&
                    reservePlayersOverviewRaw.length === 0 && reserveGoaliesOverviewRaw.length === 0 &&
                    injuredPlayers.length === 0 && sortedInjuredGoaliesOverview.length === 0) && (
                    <div className="text-center p-5">
                      <p>{language === 'fr' ? 'Aucun joueur trouvé avec les filtres actuels.' : 'No players found with current filters.'}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'players' && (
              <>
                <div className="mb-3">
                  <PositionFilterMyTeams
                    selectedPosition={selectedPosition}
                    onPositionChange={setSelectedPosition}
                    language={language}
                  />
                </div>

                {sortedPlayers.length === 0 ? (
                  <div className="text-center">
                    <p>
                      {activeSection === 'INJURED' 
                        ? 'Aucun joueur blessé disponible pour les filtres sélectionnés.'
                        : activeSection === 'SCRATCH'
                        ? 'Aucun joueur scratch disponible pour les filtres sélectionnés.'
                        : 'Aucun joueur disponible pour les filtres sélectionnés.'}
                    </p>
                  </div>
                ) : (
                  <OtherTeamsTable 
                    players={sortedPlayers} 
                    language={language} 
                    injuredPlayers={injuryDescriptions}
                    highlightPlayerId={highlightPlayerId ?? undefined}
                    highlightTerm={highlightPlayerId ? playerSearchTerm : ''}
                    onViewDetails={handleViewPlayerDetails}
                  watchlistPlayerIds={watchlistPlayerIds}
                  onAddToWatchlist={handleAddToWatchlist}
                  onRemoveFromWatchlist={handleRemoveFromWatchlist}
                  watchlistActionInProgress={watchlistActionInProgress}
                  />
                )}
              </>
            )}

            {activeTab === 'goalies' && (
              <>
                {sortedGoalies.length === 0 ? (
                  <div className="text-center">
                    <p>
                      {activeSection === 'INJURED'
                        ? 'Aucun gardien blessé disponible.'
                        : activeSection === 'SCRATCH'
                        ? 'Aucun gardien scratch disponible.'
                        : 'Aucun gardien disponible.'}
                    </p>
                  </div>
                ) : (
                  <OtherTeamsGoalieTable 
                    goalies={sortedGoalies} 
                    language={language}
                    injuredPlayers={injuryDescriptions}
                    highlightPlayerId={highlightPlayerId ?? undefined}
                    highlightTerm={highlightPlayerId ? playerSearchTerm : ''}
                    onViewDetails={handleViewPlayerDetails}
                  watchlistPlayerIds={watchlistPlayerIds}
                  onAddToWatchlist={handleAddToWatchlist}
                  onRemoveFromWatchlist={handleRemoveFromWatchlist}
                  watchlistActionInProgress={watchlistActionInProgress}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

